import React, { useEffect, useState } from 'react';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Modal from '../../../components/ui/Modal';
import QRCodeGenerator from '../../../components/shared/QRCodeGenerator';
import Spinner from '../../../components/ui/Spinner';
import { formatarMoeda as formatCurrency } from '../../../utils/currencyUtils';
import { db } from '@/config/firebase.config';

export interface Cobranca {
  id: string;
  codigo: string;
  valor: number;
  numeroComanda: string;
  unidadeId: string;
  operadorId: string;
  status: 'aberta' | 'paga' | 'cancelada';
  ultimaRecusa?: { motivo: string; em?: Timestamp };
  criadaEm?: Timestamp;
}

// Texto enviado ao cliente pelo WhatsApp (ou outro app) junto com o código.
export const mensagemDaCobranca = (cobranca: Pick<Cobranca, 'valor' | 'numeroComanda' | 'codigo'>) =>
  `Pagamento Clube Mr Tugas: ${formatCurrency(cobranca.valor)}` +
  `${cobranca.numeroComanda ? ` (comanda ${cobranca.numeroComanda})` : ''}.\n` +
  `No app, toque em "Pagar no Caixa" > "Digitar código" e informe: ${cobranca.codigo}\n` +
  `https://clubemrtugas.com.br`;

/** Celular: menu de compartilhar do sistema. Computador: WhatsApp com a mensagem pronta. */
export const compartilharCobranca = async (cobranca: Pick<Cobranca, 'valor' | 'numeroComanda' | 'codigo'>) => {
  const texto = mensagemDaCobranca(cobranca);
  // No Windows o Chrome/Edge também têm navigator.share, mas abrem o menu do
  // sistema, que pode nem listar o WhatsApp: lá vai direto para o WhatsApp.
  const celular = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (celular && navigator.share) {
    try {
      await navigator.share({ title: 'Cobrança Clube Mr Tugas', text: texto });
      return;
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') return;
    }
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
};

interface CobrancaModalProps {
  cobrancaId: string | null;
  onClose: () => void;
}

const CobrancaModal: React.FC<CobrancaModalProps> = ({ cobrancaId, onClose }) => {
  const [cobranca, setCobranca] = useState<Cobranca | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setCobranca(null);
    setErro(null);
    if (!cobrancaId) return;
    return onSnapshot(doc(db, 'cobrancas', cobrancaId), (snapshot) => {
      if (snapshot.exists()) setCobranca({ id: snapshot.id, ...snapshot.data() } as Cobranca);
    });
  }, [cobrancaId]);

  if (!cobrancaId) return null;

  const copiar = async () => {
    if (!cobranca) return;
    await navigator.clipboard.writeText(mensagemDaCobranca(cobranca));
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const cancelar = async () => {
    if (!cobranca || !window.confirm(`Cancelar a cobrança ${cobranca.codigo} de ${formatCurrency(cobranca.valor)}?`)) return;
    setCancelando(true);
    setErro(null);
    try {
      const resultado = await httpsCallable(getFunctions(), 'cancelarCobranca')({ cobrancaId });
      const { success, error } = resultado.data as { success: boolean; error?: string };
      if (!success) setErro(error || 'Não foi possível cancelar.');
    } catch {
      setErro('Não foi possível cancelar. Tente novamente.');
    } finally {
      setCancelando(false);
    }
  };

  const status = cobranca?.status;

  return (
    <Modal isOpen={!!cobrancaId} onClose={onClose} titulo="Cobrança Tugas Coins">
      <div className="p-4 md:p-6 text-center">
        {!cobranca ? (
          <div className="flex justify-center py-10"><Spinner size="lg" /></div>
        ) : (
          <>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-primary mb-2">
                {status === 'paga' ? 'Pagamento Confirmado' : status === 'cancelada' ? 'Cobrança Cancelada' : 'Aguardando Pagamento'}
              </h3>
              <p className="text-gray-300">
                Valor: <span className="font-bold text-xl text-primary">{formatCurrency(cobranca.valor)}</span>
              </p>
              {cobranca.numeroComanda && (
                <p className="text-gray-300">
                  Comanda/Mesa: <span className="font-bold">{cobranca.numeroComanda}</span>
                </p>
              )}
            </div>

            {status === 'aberta' && (
              <>
                <QRCodeGenerator
                  transacaoData={{
                    paymentIntentId: cobranca.id,
                    valor: cobranca.valor,
                    numeroComanda: cobranca.numeroComanda,
                    operadorId: cobranca.operadorId,
                    unidadeId: cobranca.unidadeId,
                    timestamp: cobranca.criadaEm?.toDate().toISOString() || '',
                  }}
                  // O QR leva os dois formatos. App novo: usa `c`/`k` e paga a cobrança no
                  // servidor. App antigo em cache: lê como QR de débito e grava o débito
                  // pendente com o id da cobrança; o gatilho debitoPendenteDeCobranca decide.
                  valorQr={JSON.stringify({
                    t: 'tucoins_debito',
                    id: cobranca.id,
                    v: cobranca.valor,
                    cmd: cobranca.numeroComanda || '',
                    op: cobranca.operadorId,
                    un: cobranca.unidadeId,
                    ts: cobranca.criadaEm?.toDate().toISOString() || '',
                    tipoOperacao: 'debito',
                    c: cobranca.id,
                    k: cobranca.codigo,
                  })}
                  titulo="Escaneie para Pagar"
                  subtitulo="Ou o cliente digita o código abaixo no app"
                />

                <div className="mt-4">
                  <p className="text-sm text-gray-300">Código de pagamento</p>
                  <p className="mt-1 py-3 rounded-lg bg-background border border-primary/30 text-4xl font-bold tracking-widest text-primary font-mono">{cobranca.codigo}</p>
                </div>

                <div className="mt-4 flex gap-3">
                  <button
                    onClick={() => compartilharCobranca(cobranca)}
                    className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
                  >
                    Compartilhar código
                  </button>
                  <button
                    onClick={copiar}
                    className="flex-1 py-2 px-4 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                  >
                    {copiado ? 'Copiado! ✔️' : 'Copiar'}
                  </button>
                </div>
              </>
            )}

            {/* Status Indicator */}
            {status === 'aberta' && cobranca.ultimaRecusa && (
              <div className="mt-4 p-3 rounded-lg border bg-red-50 border-red-200 text-left">
                <p className="text-sm font-semibold text-red-600">Pagamento recusado</p>
                <p className="text-sm text-red-600 mt-1">{cobranca.ultimaRecusa.motivo}</p>
                <p className="text-xs text-red-500 mt-2">A cobrança continua aberta. Receba de outra forma ou cancele.</p>
              </div>
            )}
            {status === 'aberta' && !cobranca.ultimaRecusa && (
              <div className="mt-4 p-3 rounded-lg border bg-primary/10 border-primary/30">
                <div className="flex items-center justify-center space-x-2">
                  <Spinner size="sm" />
                  <span className="text-sm font-medium text-primary">Aguardando o cliente pagar...</span>
                </div>
                <p className="mt-2 text-xs text-gray-300">
                  Pode fechar esta janela: a cobrança fica em "Cobranças em aberto" até ser paga ou cancelada.
                </p>
              </div>
            )}
            {status === 'paga' && (
              <div className="mt-4 p-3 rounded-lg border bg-green-50 border-green-200">
                <span className="text-sm font-medium text-green-600">✅ Pagamento confirmado com sucesso!</span>
              </div>
            )}
            {status === 'cancelada' && (
              <div className="mt-4 p-3 rounded-lg border bg-gray-50 border-gray-200">
                <span className="text-sm font-medium text-gray-600">Esta cobrança foi cancelada.</span>
              </div>
            )}
            {erro && <p className="mt-3 text-sm text-red-500">{erro}</p>}

            {/* Botões de ação */}
            <div className="mt-6 flex gap-3">
              {status === 'aberta' && (
                <button
                  onClick={cancelar}
                  disabled={cancelando}
                  className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg transition-colors font-medium"
                >
                  {cancelando ? 'Cancelando...' : 'Cancelar cobrança'}
                </button>
              )}
              <button
                onClick={onClose}
                className="flex-1 py-2 px-4 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
              >
                Fechar
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CobrancaModal;
